import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaArrowRight, FaShieldAlt, FaClock, FaLaptop } from "react-icons/fa";
import { Link } from "react-router-dom";
import Navbar from "../components/navbar";
import { HiMail, HiUser, HiChatAlt2 } from "react-icons/hi";
import { post } from "../utils/request";

const ServiceCard = ({ title, description, icon, delay }) => {
  return (
    <motion.div
      className="group relative bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-700 overflow-hidden"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay }}
      viewport={{ once: true }}
      whileHover={{ y: -8 }}
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-fg/20 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

      <div className="relative z-10">
        <div className="w-16 h-16 bg-gradient-to-br from-fg to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
          {icon && React.cloneElement(icon, { size: 32, className: "text-white" })}
        </div>

        <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
        <p className="text-gray-400 leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
};

const Landing = () => {
  const [formData, setFormData] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const handleSendClick = async () => {
    setIsSending(true);
    setIsSent(false);

    const response = await post("/../send-message", formData);
    if (response.success) {
      setIsSent(true);
      setFormData({});
    }

    setIsSending(false);
  };

  return (
    <div className="min-h-screen bg-bg">
      <Navbar />

      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <motion.div
          className="relative z-10 max-w-4xl mx-auto px-6 py-20 text-center"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
              Welcome to <span className="text-fg">KeySpyder</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-2xl mx-auto">
              Advanced keystroke monitoring and analysis platform for enhanced security
            </p>
          </motion.div>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <Link
              to="/devices"
              className="group bg-fg hover:bg-blue-600 text-white font-semibold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-3 text-lg"
            >
              Access Dashboard
              <FaArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
            <Link
              to="/about"
              className="group bg-gray-800 hover:bg-gray-700 text-white font-semibold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gray-700 hover:border-fg flex items-center gap-3 text-lg"
            >
              Meet the Team
            </Link>
          </motion.div>
        </motion.div>
      </div>

      <div className="py-20 px-6 bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Powerful Features
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Everything you need for comprehensive keystroke monitoring
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ServiceCard
              title="Real-time Monitoring"
              description="Track and analyze keystrokes from multiple devices instantly with live updates and comprehensive logging."
              icon={<FaClock />}
              delay={0.1}
            />
            <ServiceCard
              title="Enterprise Security"
              description="Military-grade encryption ensures your data remains protected with advanced security protocols."
              icon={<FaShieldAlt />}
              delay={0.2}
            />
            <ServiceCard
              title="Cross-Platform"
              description="Seamless compatibility across Windows, macOS, and Linux for complete flexibility."
              icon={<FaLaptop />}
              delay={0.3}
            />
          </div>
        </div>
      </div>

      <div className="py-20 px-6 bg-bg">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gray-800 rounded-3xl shadow-2xl overflow-hidden border border-gray-700"
          >
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-10 md:p-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Get in Touch
                </h2>
                <p className="text-gray-400 mb-8">
                  Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
                </p>

                <div className="space-y-4">
                  <div className="relative">
                    <HiUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                    <input
                      type="text"
                      placeholder="Your Name"
                      value={formData.name || ''}
                      className="w-full pl-12 pr-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl focus:outline-none focus:border-fg transition-colors text-white placeholder-gray-500"
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>

                  <div className="relative">
                    <HiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                    <input
                      type="email"
                      placeholder="Your Email"
                      value={formData.email || ''}
                      className="w-full pl-12 pr-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl focus:outline-none focus:border-fg transition-colors text-white placeholder-gray-500"
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>

                  <div className="relative">
                    <HiChatAlt2 className="absolute left-4 top-4 text-gray-500" size={20} />
                    <textarea
                      rows="4"
                      placeholder="Your Message"
                      value={formData.message || ''}
                      className="w-full pl-12 pr-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl focus:outline-none focus:border-fg transition-colors resize-none text-white placeholder-gray-500"
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    ></textarea>
                  </div>

                  <button
                    disabled={isSending}
                    className={`w-full font-semibold py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${isSending
                      ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                      : "bg-fg hover:bg-blue-600 text-white shadow-lg hover:shadow-xl"
                      }`}
                    onClick={handleSendClick}
                  >
                    {isSending ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <FaArrowRight size={18} />
                      </>
                    )}
                  </button>

                  {isSent && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-green-900/30 border border-green-700 text-green-400 px-4 py-3 rounded-xl text-center font-medium"
                    >
                      ✓ Message sent successfully! We'll get back to you soon.
                    </motion.div>
                  )}
                </div>
              </div>

              <div className="relative h-64 md:h-auto min-h-[400px]">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d235014.2991870182!2d72.4149306201118!3d23.02015808505005!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e848aba5bd449%3A0x4fcedd11614f6516!2sAhmedabad%2C%20Gujarat!5e0!3m2!1sen!2sin!4v1764847854480!5m2!1sen!2sin" width="600" height="450" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <footer className="bg-gray-950 text-white py-8 text-center border-t border-gray-800">
        <p className="text-gray-500">© 2024 KeySpyder. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Landing;
